export type DurationUnit = "MIN" | "HOUR" | "DAY";

/** A number and its unit; the component renders it through `support.staff.units.<unit>`. */
export interface DurationParts {
	value: number;
	unit: DurationUnit;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MIN = 60;
const DAY_MIN = 24 * HOUR_MIN;

const round1 = (value: number): number => Math.round(value * 10) / 10;

/** Whole units for an age ("12 min", "3 h", "2 d"); never negative. */
export const ageParts = (fromIso: string, now: Date): DurationParts => {
	const minutes = Math.max(0, Math.floor((now.getTime() - Date.parse(fromIso)) / MINUTE_MS));

	if (minutes < HOUR_MIN) {
		return { value: minutes, unit: "MIN" };
	}

	if (minutes < DAY_MIN) {
		return { value: Math.floor(minutes / HOUR_MIN), unit: "HOUR" };
	}

	return { value: Math.floor(minutes / DAY_MIN), unit: "DAY" };
};

/** One decimal above an hour ("1.4 h", "2.1 d") — the median tile. */
export const durationParts = (minutes: number): DurationParts => {
	if (minutes < HOUR_MIN) {
		return { value: Math.round(minutes), unit: "MIN" };
	}

	if (minutes < DAY_MIN) {
		return { value: round1(minutes / HOUR_MIN), unit: "HOUR" };
	}

	return { value: round1(minutes / DAY_MIN), unit: "DAY" };
};

/** "3–5" days until the earliest and latest auto-close, floored at 0; one number when equal. */
export const autoCloseWindowDays = (
	earliestIso: string | null,
	latestIso: string | null,
	now: Date
): string | null => {
	if (!earliestIso || !latestIso) {
		return null;
	}

	const daysUntil = (iso: string): number =>
		Math.max(0, Math.ceil((Date.parse(iso) - now.getTime()) / (DAY_MIN * MINUTE_MS)));
	const from = daysUntil(earliestIso);
	const to = daysUntil(latestIso);

	return from === to ? `${from}` : `${from}–${to}`;
};
